import { BehaviorSubject, combineLatest, firstValueFrom, from, lastValueFrom, merge, NEVER, timer, } from 'rxjs';
import { distinctUntilChanged, exhaustMap, filter, map, takeUntil, timeout } from 'rxjs/operators';
import { updateMapWithStableIdentity } from '../util/misc.js';
import { MonitoredBehaviorSubject, toBehaviorSubject } from '../util/rx.js';
import { getLoraxErrorCode, isDisconnectedLoraxErrorCode } from './connection-manager.js';
import { getClockAdjEpochs, isClockAdjRecord, makeRecord, makeRecords, } from './log-1-record.js';
export const mod32 = (value) => {
    const mod = 0x100000000;
    return ((value % mod) + mod) % mod;
};
const BOUNDS_READ_FREQ = 100;
const DEFAULT_GETTER_READ_FREQ = 100;
class WatchGetter {
    selected;
    loaded;
    entryParam;
    freq;
    static async create(params, def, freq) {
        const selectedParam = params.getInApiTry(def.selectedPointer);
        const loadedParam = params.getInApiTry(def.loadedPointer);
        if (!selectedParam || !loadedParam) {
            return undefined;
        }
        const entryParam = params.getInApiThrow(def.entry);
        const loadedObs = await loadedParam.watch(freq ?? DEFAULT_GETTER_READ_FREQ);
        return new WatchGetter(selectedParam, loadedParam, loadedObs, entryParam, freq);
    }
    loadedSubj = new BehaviorSubject(undefined);
    subscriptions = [];
    constructor(selected, loaded, loadedObs, entryParam, freq) {
        this.selected = selected;
        this.loaded = loaded;
        this.entryParam = entryParam;
        this.freq = freq;
        this.subscriptions.push(loadedObs.subscribe(this.loadedSubj));
    }
    async get(index) {
        if (this.loadedSubj.value !== index) {
            await this.selected.set(index, true);
            await firstValueFrom(this.loadedSubj.pipe(filter((v) => v === index), timeout(500)));
        }
        const entry = await this.entryParam.readOnce(this.freq ?? DEFAULT_GETTER_READ_FREQ);
        if (!entry) {
            throw new Error('Reading record failed');
        }
        return entry;
    }
    async teardown() {
        await this.loaded.unwatch();
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}
class LegacyGetter {
    selectedParam;
    entryParam;
    freq;
    static create(params, def, freq) {
        const selectedParam = params.getInApiTry(def.pointer);
        if (!selectedParam) {
            return undefined;
        }
        const entryParam = params.getInApiThrow(def.entry);
        return Promise.resolve(new LegacyGetter(selectedParam, entryParam, freq));
    }
    constructor(selectedParam, entryParam, freq) {
        this.selectedParam = selectedParam;
        this.entryParam = entryParam;
        this.freq = freq;
    }
    async get(index) {
        await this.selectedParam.set(index);
        const entry = await this.entryParam.readOnce(this.freq ?? DEFAULT_GETTER_READ_FREQ);
        if (!entry) {
            throw new Error('Reading record failed');
        }
        return entry;
    }
    async teardown() {
        // no-op
    }
}
/** Class for reading and monitoring version 1 logs */
export class Log1 {
    def;
    logger;
    params;
    options;
    recordSubj = new MonitoredBehaviorSubject(new Map());
    beginInt;
    endInt;
    progressSubj = new BehaviorSubject(0);
    raw = new Map();
    adjEpochs;
    index;
    isAliveSubj = new BehaviorSubject(true);
    /**
     * @param def Definition for the log, giving its index (0=audit or 1=fault) and parameter keys
     * @param logger `Logger` to use for errors
     * @param params `ParamRegistry` instance for the connection
     * @param options Options object
     */
    constructor(def, logger, params, options) {
        this.def = def;
        this.logger = logger;
        this.params = params;
        this.options = options;
        this.index = def.index;
        this.adjEpochs = getClockAdjEpochs(this.index, []);
        this.params.isAvailableObs
            .pipe(distinctUntilChanged(), filter((v) => !v))
            .subscribe(this.clear);
        this.loop().catch((error) => this.logger.error('Reading logs failed.', { error }));
    }
    /** Fraction of log entries loaded; 1 = all entries loaded */
    get progress() {
        return this.progressSubj.value;
    }
    /** `Observable` emitting fraction of log entries loaded; 1 = all entries loaded */
    get progressObs() {
        return this.progressSubj.asObservable();
    }
    /** `Map` of records loaded from the device */
    get records() {
        return this.recordSubj.value;
    }
    /** `Observable` emitting `Map`s of records loaded from the device */
    get recordsObs() {
        return this.recordSubj.valueObs;
    }
    /** Index of the first log entry available on the device when this was created, if known */
    get begin() {
        return this.beginInt;
    }
    /** Index one past the last log entry available, if known, as of creation or last record subject subscription time (whichever is later) */
    get end() {
        return this.endInt;
    }
    /** Number of entries available on the device */
    get count() {
        if (this.beginInt === undefined || this.endInt === undefined) {
            return undefined;
        }
        return mod32(this.endInt - this.beginInt);
    }
    /** Stop any ongoing process and release resources */
    dispose() {
        this.isAliveSubj.next(false);
    }
    // if not connected and unlocked, wait until we are
    // if no subscribers, wait for some
    get isWanted() {
        return this.params.isAvailable && this.recordSubj.subscriberCount && this.isAliveSubj.value;
    }
    get isWantedObs() {
        return combineLatest([
            this.params.isAvailableObs,
            this.recordSubj.subscriberCountObs,
            this.isAliveSubj,
        ]).pipe(map(([available, count, alive]) => available && !!count && alive));
    }
    loop = async () => {
        for (;;) {
            const wantedOrDisposed = merge(this.isWantedObs.pipe(filter((v) => v), map((_) => 'wanted')), this.isAliveSubj.pipe(filter((v) => !v), map((_) => 'disposed')));
            const status = await firstValueFrom(wantedOrDisposed);
            if (status === 'disposed') {
                break;
            }
            let getter;
            try {
                const beginParam = this.params.getInApiThrow(this.def.begin);
                const endParam = this.params.getInApiThrow(this.def.end);
                getter =
                    (await WatchGetter.create(this.params, this.def, this.options.readFreq)) ??
                        (await LegacyGetter.create(this.params, this.def, this.options.readFreq));
                if (!getter) {
                    throw new Error('Unable to determine how to read logs');
                }
                await this.initialLoad(beginParam, endParam, getter);
                if (!this.options.updateDuringInit) {
                    this.updateEpochsAndRecords();
                }
                await this.monitor(endParam, getter);
            }
            catch (error) {
                if (!isDisconnectedLoraxErrorCode(getLoraxErrorCode(error))) {
                    this.logger.error('Log monitoring failed', { error });
                }
            }
            finally {
                try {
                    await getter?.teardown();
                }
                catch (error) {
                    this.logger.error('Log getter teardown failed', { error });
                }
            }
            // wait for disconnect, or delay, to avoid infinite loop
            const delayObs = this.options.retryDelay === undefined ? NEVER : timer(this.options.retryDelay);
            await firstValueFrom(merge(delayObs, this.isWantedObs.pipe(filter((v) => !v))));
        }
    };
    /** Set beginInt and endInt, and then load records in [beginInt, endInt) */
    initialLoad = async (beginParam, endParam, getter) => {
        const devBegin = await beginParam.readOnce(BOUNDS_READ_FREQ);
        const devEnd = await endParam.readOnce(BOUNDS_READ_FREQ);
        // check if startIndex is outside of the range [begin, end], considering rollover
        // if it's outside of that range, then ignore it (maybe lots of log entries were created on the device since last scraped?)
        this.beginInt =
            this.options.startIndex !== undefined &&
                mod32(this.options.startIndex - devBegin) <= mod32(devEnd - devBegin)
                ? this.options.startIndex
                : devBegin;
        this.endInt = devEnd;
        const count = this.count;
        const { start, end, delta } = this.options.initReverse
            ? { start: mod32(this.endInt - 1), end: mod32(this.beginInt - 1), delta: -1 } // reverse order
            : { start: this.beginInt, end: this.endInt, delta: 1 }; // forward order
        for (let index = start; index != end; index = mod32(index + delta)) {
            if (!this.isWanted) {
                this.clear();
                return;
            }
            // get the raw record
            const raw = await getter.get(index);
            this.raw.set(index, raw);
            if (this.options.updateDuringInit) {
                // update the formatted records
                this.updateRecords(index, raw);
            }
            // update progress if not complete (don't declare complete until monitoring starts)
            if (this.raw.size < count) {
                this.progressSubj.next(this.raw.size / count);
            }
        }
    };
    monitor = async (endParam, getter) => {
        // subscribe to the end param, and wait until either it emits a value or the log's no longer wanted
        const endObs = await endParam.startObs();
        const unwantedObs = this.isWantedObs.pipe(filter((v) => !v));
        // observable that emits values when a valid end update is received, and ends when no longer wanted
        const wantedEndObs = endObs.pipe(takeUntil(unwantedObs));
        const wantedEnd = await toBehaviorSubject(wantedEndObs);
        if (!wantedEnd) {
            return; // observable completed before emitting
        }
        const readLatest = async () => {
            // once a value's emitted: if it matches endInt, emit progress 100%, and wait for another
            // if it doesn't match endInt, then read the new log entries, emitting progress as we go and stopping if unwanted
            // once done reading, wait for a new value
            if (this.beginInt === undefined || this.endInt === undefined) {
                throw new Error('Cannot call monitor() now');
            }
            for (let index = this.endInt; this.isWanted; index = mod32(index + 1)) {
                this.endInt = wantedEnd.subject.value;
                this.progressSubj.next(mod32(index - this.beginInt) / this.count);
                if (index === this.endInt) {
                    break;
                }
                const raw = await getter.get(index);
                this.raw.set(index, raw);
                // update the formatted records
                this.updateRecords(index, raw);
            }
        };
        try {
            await lastValueFrom(wantedEnd.subject.pipe(exhaustMap((_) => from(readLatest()))));
        }
        finally {
            wantedEnd.subscription.unsubscribe();
        }
    };
    updateRecords(index, raw) {
        if (isClockAdjRecord(this.index, raw)) {
            this.updateEpochsAndRecords();
        }
        else {
            this.addOneRecord(index, raw);
        }
    }
    addOneRecord(index, raw) {
        if (this.beginInt === undefined) {
            throw new Error('Cannot call addOneRecord() now');
        }
        const record = makeRecord(this.index, { buffer: raw, index }, index - this.beginInt, this.adjEpochs);
        const records = this.recordSubj.value;
        records.set(index, record);
        this.recordSubj.next(records);
    }
    updateEpochsAndRecords() {
        // recalculate epochs
        const raw = Array.from(this.raw.entries())
            .map((e) => ({ index: e[0], buffer: e[1] }))
            .sort((a, b) => a.index - b.index);
        this.adjEpochs = getClockAdjEpochs(this.index, raw);
        // apply epochs to the records
        const newRecords = new Map(makeRecords(this.index, raw, this.adjEpochs).map((e) => [e.recordIndex, e]));
        // update records subject while maintaining object identity when possible (to avoid UI flicker)
        const records = this.recordSubj.value;
        updateMapWithStableIdentity(records, newRecords);
        this.recordSubj.next(records);
    }
    clear = () => {
        this.progressSubj.next(0);
        this.beginInt = undefined;
        this.endInt = undefined;
        this.raw.clear();
        this.adjEpochs = getClockAdjEpochs(this.index, []);
        this.recordSubj.next(new Map());
    };
}
export const auditLog1Def = {
    index: 0,
    pointer: 'auditLogPointer',
    loadedPointer: 'auditLogLoadedPointer',
    selectedPointer: 'auditLogSelectedPointer',
    entry: 'auditLogEntry',
    begin: 'auditLogBegin',
    end: 'auditLogEnd',
};
export const faultLog1Def = {
    index: 1,
    pointer: 'faultLogPointer',
    loadedPointer: 'faultLogLoadedPointer',
    selectedPointer: 'faultLogSelectedPointer',
    entry: 'faultLogEntry',
    begin: 'faultLogBegin',
    end: 'faultLogEnd',
};
export const log1Defs = [auditLog1Def, faultLog1Def];
//# sourceMappingURL=log-1.js.map